import React, { useState, useEffect } from "react";
import {
  Typography,
  Checkbox,
  TextField,
  FormControlLabel,
  Box,
  CircularProgress,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoPopover from "./infoPopover";
import { STATUS } from "../../utils/status";

interface Config {
  id: string;
  label: string;
  type: string;
  interactive: boolean;
  identifier?: string;
  name?: string;
}

interface Stage {
  id: string;
  title: string;
  configs?: Config[];
}

interface Props {
  stages?: Stage[];
  onPreview: (data: Stage[]) => void;
  onSave: (data: any[]) => void;
  data?: {
    results?: {
      id: number;
      name: string;
      category: string;
    }[];
  };
  status?: string;
  stageTaskData: any;
}

const ConfigsToReview: React.FC<Props> = ({
  stages,
  onSave,
  onPreview,
  data,
  status,
  stageTaskData,
}) => {
  const [stageData, setStageData] = useState<Stage[]>([]);
  const [selectedStages, setSelectedStages] = useState<Record<string, boolean>>(
    {}
  );
  const [customTitles, setCustomTitles] = useState<Record<string, string>>({});
  const [dragItem, setDragItem] = useState<{
    stageId: string;
    index: number;
  } | null>(null);
  const [popoverText, setPopoverText] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(true);

  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  // Step 1: Initialize StageData
  useEffect(() => {
    if (data?.results && Array.isArray(data.results)) {
      const formattedStages: Stage[] = data.results.map((stage) => ({
        id: String(stage.id),
        title: stage.name,
        configs: [],
      }));
      setStageData(formattedStages);
    }
    setLoading(false);
  }, [data, stages]);

  // Step 2: Pre-select based on stageTaskData.stage_configs_list
  useEffect(() => {
    if (stageTaskData?.stage_configs_list?.length > 0) {
      const initialSelected: Record<string, boolean> = {};
      const initialTitles: Record<string, string> = {};

      stageTaskData.stage_configs_list.forEach((cfg: any) => {
        const stageId = String(cfg.stage_id);
        initialSelected[stageId] = true;
        initialTitles[stageId] = cfg.title || "";
      });

      setSelectedStages(initialSelected);
      setCustomTitles(initialTitles);
    }
  }, [stageTaskData]);

  // Step 3: Update onSave with selected + titled stages only
  useEffect(() => {
    const filteredStages = stageData.filter(
      (stage) => selectedStages[stage.id]
    );

    const formattedData = filteredStages.map((stage, idx) => ({
      index: idx + 1,
      stage_id: Number(stage.id),
      title: customTitles[stage.id] || stage.title,
    }));

    onSave(formattedData);
  }, [stageData, selectedStages, customTitles, onSave]);

  const handleStageDragStart = (
    index: number,
    stageId: string,
    e: React.DragEvent
  ) => {
    setDragItem({ stageId, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleStageDrop = (dropIndex: number, e: React.DragEvent) => {
    e.preventDefault();
    if (!dragItem) return;
    if (dragItem.index === dropIndex) {
      setDragItem(null);
      return;
    }

    const reordered = [...stageData];
    const [movedStage] = reordered.splice(dragItem.index, 1);
    reordered.splice(dropIndex, 0, movedStage);

    setStageData(reordered);
    setDragItem(null);
  };

  const handleInfoClick = (
    event: React.MouseEvent<HTMLElement>,
    text: string
  ) => {
    setAnchorEl(event.currentTarget);
    setPopoverText(text);
  };

  if (loading || status === STATUS.PENDING) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box>
        <div style={{ width: "30%", minWidth: "300px" }}>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              color: "#10383D",
            }}
          >
            Stages to Review
            <InfoOutlinedIcon
              sx={{ marginLeft: "10px", fontSize: "13px", cursor: "pointer" }}
              onClick={(e: any) =>
                handleInfoClick(
                  e,
                  "List of all stages available for configuration review."
                )
              }
            />
          </Typography>
        </div>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Drag and drop stages to reorder
        </Typography>

        {!stageData ||
          (stageData.length === 0 && (
            <Box>
              <Typography variant="body2" color="red">
                No stage configurations available. Create stages and connect your workflow to view and select stages.
              </Typography>
            </Box>
          ))}

        {stageData.map((stage, idx) => (
          <Box
            key={stage.id}
            draggable
            onDragStart={(e) => handleStageDragStart(idx, stage.id, e)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleStageDrop(idx, e)}
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              p: 2,
              border: "2px dashed transparent",
              borderRadius: 2,
              background: dragItem?.stageId === stage.id ? "#f9f9f9" : "white",
              "&:hover": {
                border: "2px dashed #aaa",
              },
              cursor: "move",
              userSelect: "none",
            }}
          >
            <DragIndicatorIcon
              sx={{
                mr: 1,
                color: "grey.500",
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!selectedStages[stage.id]}
                  onChange={(e) =>
                    setSelectedStages((prev) => ({
                      ...prev,
                      [stage.id]: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography>{stage.title}</Typography>}
            />

            {selectedStages[stage.id] && (
              <TextField
                placeholder="Enter Title here..."
                value={customTitles[stage.id] || ""}
                size="small"
                onChange={(e) =>
                  setCustomTitles((prev) => ({
                    ...prev,
                    [stage.id]: e.target.value,
                  }))
                }
                sx={{ ml: 2, width: "250px" }}
              />
            )}
          </Box>
        ))}
      </Box>

      <InfoPopover
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        text={popoverText}
      />
    </>
  );
};

export default ConfigsToReview;
